'use client';

import {
    TypeFormFieldMessageWithoutUnresolvableLinksResponse,
    TypeFormFieldsWithoutUnresolvableLinksResponse,
    TypeFormHousingWithoutUnresolvableLinksResponse,
    TypeSettingsWithoutUnresolvableLinksResponse,
    TypeSubformWithoutUnresolvableLinksResponse,
} from '@/features/contentful/_generated';
import React, { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FormField from './FormField';
import { EMAIL_REGEX, PHONE_REGEX } from '@/config/constants';
import { useSettingsContext } from '@/contexts/SettingsContext';
import downloadFile from './download';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import MainBodyToReact from '@/features/contentful/components/MainBodyToReact';
import classNames from 'classnames';
import GCapcha from '@/components/Form/GCapcha';
import ReCAPTCHA from 'react-google-recaptcha';
import createSettingsObject from '@/utils/createSettingsObject';
import { getAbsoluteLocalizedLink } from '@/utils/getLocalizedLink';

interface IFormHousingProps {
    formData: TypeFormHousingWithoutUnresolvableLinksResponse;
    formId?: string;
    closeModal?: () => void;
}

const errorInfoClass = 'tw-text-red-500 tw-text-[13px] tw-font-gotham-bold tw-mt-1';

const getAdditionalPardotFields = (
    formData: TypeFormHousingWithoutUnresolvableLinksResponse,
    lang: string,
) => [
    {
        sys: {
            id: 'endURL',
            contentType: {
                sys: {
                    id: 'formFields',
                },
            },
        },
        fields: {
            fieldName: 'endURL',
            label: 'endURL',
            type: 'hidden',
            value: formData?.fields?.action,
            cssId: 'endURL',
        },
    },
    {
        sys: {
            id: 'success',
            contentType: {
                sys: {
                    id: 'formFields',
                },
            },
        },
        fields: {
            fieldName: 'success_location',
            label: 'success_location',
            type: 'hidden',
            value: getAbsoluteLocalizedLink(formData?.fields?.successUrl, lang),
            cssId: 'success_location',
        },
    },
];

const getReturnUrlContactUs = (
    formData: TypeFormHousingWithoutUnresolvableLinksResponse,
    lang: string,
) => {
    return {
        sys: {
            id: 'retURL',
            contentType: {
                sys: {
                    id: 'formFields',
                },
            },
        },
        fields: {
            fieldName: 'retURL',
            label: 'retURL',
            type: 'hidden',
            value: getAbsoluteLocalizedLink(formData?.fields?.successUrl, lang),
            cssId: 'redirect-url',
        },
    };
};

const FormHousing: React.FC<IFormHousingProps> = ({ formData, formId }) => {
    const inspectorProps = useContentfulInspectorMode({ entryId: formId });
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [formValues, setFormValues] = useState<{ [key: string]: string }>({});
    const [submitPossible, setSubmitPossible] = useState<boolean>(true);
    const { getLabel, lang } = useSettingsContext();
    const [captcha, setCaptcha] = useState<string>();
    const recaptchaRef: RefObject<ReCAPTCHA> = useRef(null);
    const formRef = useRef<HTMLFormElement>(null);

    const formSettings = createSettingsObject(
        formData?.fields?.settings as TypeSettingsWithoutUnresolvableLinksResponse[],
    );
    const pleaseCompleteLabel = getLabel('label', 'pleaseComplete', formSettings);
    const invalidEmailLabel = getLabel('label', 'invalidEmail', formSettings);
    const invalidPhoneNumberLabel = getLabel('label', 'invalidPhoneNumber', formSettings);
    const pleaseSelectLabel = getLabel('label', 'pleaseSelect', formSettings);

    const isPardot = formData?.fields?.type === 'Pardot';

    const relatedFields = useMemo(() => {
        const fields: any[] = formData?.fields?.field || [];
        const relatedFields: { [key: string]: { [key: string]: string } } = {};

        const processFields = (fieldsArray: any[]) => {
            fieldsArray.forEach((field) => {
                if (field?.sys?.contentType?.sys?.id === 'formFields') {
                    const name = field?.fields?.name;
                    if (name?.startsWith('rel.')) {
                        const [, parentFieldName, parentFieldValue] = name.split('.');
                        const sanitizedValue = parentFieldValue.replace(/\s+/g, '');
                        if (!relatedFields[parentFieldName]) {
                            relatedFields[parentFieldName] = {};
                        }
                        relatedFields[parentFieldName][sanitizedValue] = name;
                    }
                } else if (field?.sys?.contentType?.sys?.id === 'subform') {
                    const subformFields = field?.fields?.field || [];
                    processFields(subformFields);
                }
            });
        };

        processFields(fields);
        return relatedFields;
    }, [formData?.fields?.field]);

    useEffect(() => {
        const initialFormValues: { [key: string]: string } = {};
        const fieldsToTrigger: string[] = [];

        formData?.fields?.field?.forEach((field: any) => {
            if (field?.sys?.contentType?.sys?.id === 'formFields' && field.fields?.value) {
                initialFormValues[field.fields.fieldName] = field.fields.value;
                if (field.fields.type === 'select') {
                    fieldsToTrigger.push(field.fields.fieldName);
                }
            }
        });

        setFormValues(initialFormValues);

        setTimeout(() => {
            fieldsToTrigger.forEach((fieldName) => {
                const element = formRef.current?.elements.namedItem(fieldName) as HTMLElement;
                if (element) {
                    element.dispatchEvent(new Event('change', { bubbles: true }));
                }
            });
        }, 0);
    }, [formData?.fields?.field]);

    useEffect(() => {
        let canSubmit = true;
        formData?.fields?.field?.forEach((field: any) => {
            if (field?.sys?.contentType?.sys?.id === 'subform') {
                const { relatedFieldName, relatedFieldValue, possibleToSubmit } = field.fields;
                if (
                    formValues[relatedFieldName] === relatedFieldValue &&
                    possibleToSubmit === false
                ) {
                    canSubmit = false;
                }
            }
        });
        setSubmitPossible(canSubmit);
    }, [formValues, formData?.fields?.field]);

    const handleFormSubmit = useCallback(
        async (form: HTMLFormElement) => {
            try {
                if (isPardot && formData?.fields?.downloadFile) {
                    const downloadLinkUrl = formData?.fields?.downloadFile?.fields?.file?.url
                        ? 'https:' + formData?.fields?.downloadFile?.fields?.file?.url
                        : '';
                    await downloadFile(downloadLinkUrl);
                    window.dataLayer &&
                        window.dataLayer.push({
                            event: 'cta_click',
                            cta_type: 'brochure download',
                        });
                    window.dataLayer &&
                        window.dataLayer.push({
                            event: 'brochure_download',
                            brochure_url: downloadLinkUrl,
                        });
                }

                form.requestSubmit();
            } catch (error) {
                console.error('Form submission error:', error);
            }
        },
        [formData?.fields, isPardot],
    );

    const handleSubmitForm = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            const form = e.currentTarget.form;
            e.preventDefault();
            if (!form) return;

            const newErrors: { [key: string]: string } = {};
            const elements = Array.from(form.elements) as HTMLInputElement[];

            elements.forEach((inputElement) => {
                if (inputElement.required && !inputElement.value.trim()) {
                    newErrors[inputElement.name] = pleaseCompleteLabel;
                } else if (
                    inputElement.type === 'email' &&
                    inputElement.value &&
                    !EMAIL_REGEX.test(inputElement.value)
                ) {
                    newErrors[inputElement.name] = invalidEmailLabel;
                } else if (
                    inputElement.id === 'txtPhone' &&
                    inputElement.value &&
                    !PHONE_REGEX.test(inputElement.value)
                ) {
                    newErrors[inputElement.name] = invalidPhoneNumberLabel;
                }
            });

            if (formData.fields.captcha && !captcha) {
                newErrors['captcha'] = pleaseCompleteLabel;
            } else {
                delete newErrors['captcha'];
            }

            if (Object.keys(newErrors).length === 0) {
                handleFormSubmit(form);
            } else {
                setErrors(newErrors);
            }
        },
        [
            invalidEmailLabel,
            invalidPhoneNumberLabel,
            pleaseCompleteLabel,
            handleFormSubmit,
            captcha,
            formData.fields.captcha,
        ],
    );

    const validateCaptcha = useCallback(
        (token?: string) => {
            if (formData.fields.captcha) {
                if (!token) {
                    setErrors((prevErrors) => ({
                        ...prevErrors,
                        captcha: pleaseCompleteLabel,
                    }));
                } else {
                    setCaptcha(token);
                    setErrors((prevErrors) => {
                        const newErrors = { ...prevErrors };
                        delete newErrors.captcha;
                        return newErrors;
                    });
                }
            }
        },
        [formData.fields.captcha, pleaseCompleteLabel, setCaptcha],
    );

    const validateOnChangeEvent = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const { name, value, required, type, id } = e.currentTarget;

            setFormValues((prevValues) => ({
                ...prevValues,
                [name]: relatedFields[name] ? value.replace(/\s+/g, '') : value,
            }));

            if (!Object.keys(errors).length) return;

            setErrors((prevErrors) => {
                const newErrors = { ...prevErrors };

                if (required && !value.trim()) {
                    newErrors[name] = pleaseCompleteLabel;
                } else if (type === 'email' && value && !EMAIL_REGEX.test(value)) {
                    newErrors[name] = invalidEmailLabel;
                } else if (id === 'txtPhone' && value && !PHONE_REGEX.test(value)) {
                    newErrors[name] = invalidPhoneNumberLabel;
                } else {
                    delete newErrors[name];
                }

                return newErrors;
            });
        },
        [pleaseCompleteLabel, errors, invalidEmailLabel, invalidPhoneNumberLabel, relatedFields],
    );

    const renderFormField = useCallback(
        (field: TypeFormFieldsWithoutUnresolvableLinksResponse) => {
            const fieldName = field?.fields?.name ?? '';
            const isRelatedField = fieldName.startsWith('rel.');

            if (isRelatedField) {
                const [, parentFieldName, parentFieldValue] = fieldName.split('.');
                const currentParentValue = formValues[parentFieldName];

                if (currentParentValue !== parentFieldValue.replace(/\s+/g, '')) {
                    return null;
                }
            }

            return (
                <FormField
                    formField={field}
                    key={field?.sys?.id}
                    error={errors[field?.fields?.fieldName ?? '']}
                    validateOnChangeEvent={validateOnChangeEvent}
                    id={field?.sys?.id}
                    pleaseSelectLabel={pleaseSelectLabel}
                />
            );
        },
        [errors, validateOnChangeEvent, formValues, pleaseSelectLabel],
    );

    const renderFormFieldMessage = useCallback(
        (field: TypeFormFieldMessageWithoutUnresolvableLinksResponse) => {
            const { relatedFieldName, relatedFieldValue, message } = field.fields;
            if (formValues[relatedFieldName] === relatedFieldValue) {
                return (
                    <MainBodyToReact
                        key={field.sys.id}
                        body={message}
                        classes={{
                            p: 'tw-text-[#4b4f54] tw-font-gotham-medium tw-leading-[29px] tw-pb-8 tw-font-normal tw-text-[14px]',
                        }}
                    />
                );
            }
            return null;
        },
        [formValues],
    );

    const renderSubform = useCallback(
        (subform: TypeSubformWithoutUnresolvableLinksResponse) => {
            const { relatedFieldName, relatedFieldValue, field, possibleToSubmit } = subform.fields;
            if (formValues[relatedFieldName] === relatedFieldValue) {
                return field.map((subField) => {
                    if (subField?.sys.contentType.sys.id === 'formFields') {
                        return renderFormField(
                            subField as TypeFormFieldsWithoutUnresolvableLinksResponse,
                        );
                    } else if (subField?.sys.contentType.sys.id === 'formFieldMessage') {
                        return renderFormFieldMessage(
                            subField as TypeFormFieldMessageWithoutUnresolvableLinksResponse,
                        );
                    }
                    return null;
                });
            }
            return null;
        },
        [formValues, renderFormField, renderFormFieldMessage],
    );

    const formItems = !isPardot
        ? [
              ...(formData?.fields?.field ?? []).filter(
                  (field) => (field?.fields as any).fieldName !== 'retURL',
              ),
              getReturnUrlContactUs(formData, lang),
          ]
        : [
              ...(formData?.fields?.field ?? []).filter(
                  (field) => (field?.fields as any).fieldName !== 'success_location',
              ),
              ...getAdditionalPardotFields(formData, lang),
          ];

    return (
        <>
            <h2
                className={classNames(
                    'tw-text-textBlue tw-text-center tw-mt-3 tw-font-gotham-bold',
                    {
                        'tw-pb-11 tw-text-[32px] tw-leading-[44px]': isPardot,
                        'tw-text-[24px] tw-leading-[26px]': formData?.fields?.type === 'Web2Lead',
                    },
                )}
                {...inspectorProps({
                    fieldId: 'title',
                })}
            >
                {formData?.fields?.title}
            </h2>
            <form
                ref={formRef}
                method="post"
                action={formData.fields.action}
                {...inspectorProps({
                    fieldId: 'field',
                })}
            >
                {formItems?.map((f) => {
                    if (f?.sys.contentType.sys.id === 'formFields') {
                        return renderFormField(f as TypeFormFieldsWithoutUnresolvableLinksResponse);
                    } else if (f?.sys.contentType.sys.id === 'formFieldMessage') {
                        return renderFormFieldMessage(
                            f as TypeFormFieldMessageWithoutUnresolvableLinksResponse,
                        );
                    } else if (f?.sys.contentType.sys.id === 'subform') {
                        return renderSubform(f as TypeSubformWithoutUnresolvableLinksResponse);
                    }
                    return null;
                })}

                {submitPossible && formData.fields.captcha ? (
                    <div
                        {...inspectorProps({
                            fieldId: 'captcha',
                        })}
                    >
                        <GCapcha
                            recaptchaRef={recaptchaRef}
                            onChange={(token?: string) => {
                                validateCaptcha(token);
                            }}
                        />
                        {errors['captcha'] && (
                            <span className={errorInfoClass}>{errors['captcha']}</span>
                        )}
                    </div>
                ) : null}

                {submitPossible && formData.fields?.preSubmitText ? (
                    <div
                        {...inspectorProps({
                            fieldId: 'preSubmitText',
                        })}
                    >
                        <MainBodyToReact
                            body={formData.fields.preSubmitText}
                            classes={{ p: 'tw-pt-5 !tw-pb-0 tw-text-sm tw-leading-[29px]' }}
                            hardLinksTarget="_blank"
                        />
                    </div>
                ) : null}

                {submitPossible && (
                    <div
                        className={classNames('tw-flex tw-py-4', {
                            'tw-justify-center': isPardot,
                        })}
                    >
                        <button
                            type="submit"
                            className="custom-btn custom-btn--pink custom-btn--text"
                            onClick={handleSubmitForm}
                            {...inspectorProps({
                                fieldId: 'buttonTitle',
                            })}
                        >
                            {formData?.fields?.buttonTitle}
                        </button>
                    </div>
                )}
            </form>
        </>
    );
};

export default FormHousing;
